import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { useSuccessNotification } from '../../../components/SuccessNotification';
import { adminApi } from '../../../features/admin/admin.api';
import { toDisplayMessage, toFieldErrors } from '../../../lib/error-message';
import { routeTo } from '../../../routes/paths';

import styles from './AdminPortal.module.css';

/**
 * Creates a staff account.
 *
 * There is no password field, and that is the point: the API creates the
 * account with no usable credential and emails a reset link, so nobody but the
 * account holder ever knows the password.
 */
export function AdminUserCreatePage(): React.JSX.Element {
  const navigate = useNavigate();
  const { notify, dismiss } = useSuccessNotification();
  const [email, setEmail] = useState('');
  const [roleCodes, setRoleCodes] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: () => adminApi.listRoles() });

  const create = useMutation({
    mutationFn: () => adminApi.createStaffUser({ email, roleCodes, reason }),
    onMutate: () => {
      dismiss();
    },
    onSuccess: (created) => {
      notify(
        'Staff account created. A password-setup message was requested. Email delivery is not confirmed.',
      );
      void navigate(routeTo.adminUser(created.id));
    },
    onError: (error: unknown) => {
      setFieldErrors(toFieldErrors(error));
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFieldErrors({});
    create.mutate();
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Create a staff user</h1>
      </header>

      <section className={styles.card} aria-labelledby="create-heading">
        <h2 className={styles.cardTitle} id="create-heading">
          Account details
        </h2>

        <Alert tone="info">
          No initial password is set here. A password-setup message is requested after the account
          is created. Ask them to check their inbox and spam or junk folder. Creating the account
          does not confirm that the message was delivered.
        </Alert>

        {create.isError && <Alert tone="error">{toDisplayMessage(create.error)}</Alert>}

        <form onSubmit={handleSubmit} noValidate>
          <FormField
            label="Email address"
            type="email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            autoComplete="off"
            error={fieldErrors.email}
            required
          />

          <fieldset className={styles.checkboxGrid}>
            <legend className={styles.legend}>Roles</legend>

            {roles.data?.items
              .filter((role) => role.code !== 'CUSTOMER')
              .map((role) => (
                <label className={styles.checkboxRow} key={role.id}>
                  <input
                    type="checkbox"
                    checked={roleCodes.includes(role.code)}
                    onChange={(event) => {
                      setRoleCodes((current) =>
                        event.target.checked
                          ? [...current, role.code]
                          : current.filter((code) => code !== role.code),
                      );
                    }}
                  />
                  <span>
                    {role.name} <code>{role.code}</code>
                  </span>
                </label>
              ))}
          </fieldset>

          <FormField
            label="Reason for creating this account"
            name="reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            hint="Recorded in the audit trail."
            error={fieldErrors.reason}
            required
          />

          <div className={styles.actions}>
            <Button type="submit" busy={create.isPending}>
              Create user
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
